'use client';

import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

interface RequirementToggleProps {
  label: string;
  description?: string;
  value: {
    must: boolean;
    preferred: boolean;
  };
  onChange: (value: { must: boolean; preferred: boolean }) => void;
}

export default function RequirementToggle({
  label,
  description,
  value,
  onChange,
}: RequirementToggleProps) {
  const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string[]) => {
    onChange({
      must: newValue.includes('must'),
      preferred: newValue.includes('preferred'),
    });
  };

  const selectedValues = [
    ...(value.must ? ['must'] : []),
    ...(value.preferred ? ['preferred'] : []),
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1" gutterBottom>
          {label}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <ToggleButtonGroup
        value={selectedValues}
        onChange={handleChange}
        aria-label={`${label} requirements`}
        size="small"
      >
        <ToggleButton
          value="must"
          aria-label="must have"
          color="primary"
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
          }}
        >
          Must Have
        </ToggleButton>
        <ToggleButton
          value="preferred"
          aria-label="preferred"
          color="secondary"
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'secondary.main',
              color: 'secondary.contrastText',
              '&:hover': {
                backgroundColor: 'secondary.dark',
              },
            },
          }}
        >
          Preferred
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
